import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody,
  Button, VStack, HStack, Text, Box, Spinner, IconButton, Flex, Link,
  Tabs, TabList, Tab, TabPanels, TabPanel,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon, ExternalLinkIcon, CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useClientInstallConfig, clientInstallAvailable } from "./client/ClientInstallInstructions";
import BriefClientSetup from "./client/BriefClientSetup";

// Official brand marks. Geometry from Simple Icons (Replit, Claude, Figma,
// Google) and the Lovable brand SVG. Rendered in currentColor so the tile
// controls the fill.
const BrandIcon = ({ viewBox, paths, fillRule }) => (
  <svg viewBox={viewBox} fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" aria-hidden="true">
    {paths.map((d, i) => <path key={i} d={d} fillRule={fillRule} clipRule={fillRule} />)}
  </svg>
);

const FigmaIcon = () => <BrandIcon viewBox="0 0 24 24" paths={["M15.852 8.981h-4.588V0h4.588c2.476 0 4.49 2.014 4.49 4.49s-2.014 4.491-4.49 4.491zM12.735 7.51h3.117c1.665 0 3.019-1.355 3.019-3.019s-1.355-3.019-3.019-3.019h-3.117V7.51zm0 1.471H8.148c-2.476 0-4.49-2.014-4.49-4.49S5.672 0 8.148 0h4.588v8.981zm-4.587-7.51c-1.665 0-3.019 1.355-3.019 3.019s1.354 3.02 3.019 3.02h3.117V1.471H8.148zm4.587 15.019H8.148c-2.476 0-4.49-2.014-4.49-4.49s2.014-4.49 4.49-4.49h4.588v8.98zM8.148 8.981c-1.665 0-3.019 1.355-3.019 3.019s1.355 3.019 3.019 3.019h3.117V8.981H8.148zM8.172 24c-2.489 0-4.515-2.014-4.515-4.49s2.014-4.49 4.49-4.49h4.588v4.441c0 2.503-2.047 4.539-4.563 4.539zm-.024-7.51a3.023 3.023 0 0 0-3.019 3.019c0 1.665 1.365 3.019 3.044 3.019 1.705 0 3.093-1.376 3.093-3.068v-2.97H8.148zm7.704 0h-.098c-2.476 0-4.49-2.014-4.49-4.49s2.014-4.49 4.49-4.49h.098c2.476 0 4.49 2.014 4.49 4.49s-2.014 4.49-4.49 4.49zm-.097-7.509c-1.665 0-3.019 1.355-3.019 3.019s1.355 3.019 3.019 3.019h.098c1.665 0 3.019-1.355 3.019-3.019s-1.355-3.019-3.019-3.019h-.098z"]} />;
const GoogleIcon = () => <BrandIcon viewBox="0 0 24 24" paths={["M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"]} />;

// Design tools outside the install flow. Figma Make connects to the plan with
// its own link; Stitch has no connector, so it starts from a prompt.
const DESIGN_TOOLS = [
  {
    id: "figma-make", label: "Figma Make", logo: <FigmaIcon />, bg: "#F24E1E",
    desc: "Figma Make designs with your strategic direction in context. Audience, constraints, and goals inform every screen.",
    docs: "integrate-figma-make", docsLabel: "Full Figma Make setup guide",
    where: "Add context → Connectors → Manage → Created by you → Create.",
  },
  {
    id: "google-stitch", label: "Google Stitch", logo: <GoogleIcon />, bg: "#4285F4", promptOnly: true,
    desc: "Stitch doesn't support MCP yet. Use the starter prompt to load your strategy as context before designing.",
    docs: "integrate-google-stitch", docsLabel: "Full Google Stitch guide",
  },
];

// Callers can still name a tool. Build tools open in the install flow under
// their client ids; design tools open in their own section.
const INSTALL_CLIENT_FOR = { "claude-code": "claude", claude: "claude", codex: "codex", cursor: "cursor", replit: "replit", lovable: "lovable", bolt: "bolt", v0: "v0" };

// ── presentational pieces ──────────────────────────────────────────────────
// These live at module scope on purpose. Defining them inside McpModal gives
// them a new identity on every render, which remounts them and throws away
// their state (it made the tool dropdown close the instant it opened).

const ToolTile = ({ meta, size }) => (
  <Box
    w={size} h={size} borderRadius="7px" bg={meta.bg} color="white"
    display="flex" alignItems="center" justifyContent="center" flexShrink={0}
    p={size === "28px" ? "6px" : "5px"}
  >
    {meta.logo}
  </Box>
);

const StepLabel = ({ num, label }) => (
  <HStack spacing={3} mb={5} align="baseline">
    <Text fontSize="sm" fontWeight="800" color="brand.400" letterSpacing="widest">
      {num}
    </Text>
    <Text fontSize="lg" fontWeight="700" color="gray.800" letterSpacing="tight">{label}</Text>
  </HStack>
);

const FieldLabel = ({ children }) => (
  <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="widest" color="gray.400" mb={2}>
    {children}
  </Text>
);

// One copy-ready artifact. This is the thing the user actually needs.
const CopyField = ({ value, multiline = false }) => {
  const [fieldCopied, setFieldCopied] = useState(false);
  const toast = useToast();
  const copyValue = async () => {
    await navigator.clipboard.writeText(value);
    setFieldCopied(true);
    toast({ title: "Copied", status: "success", duration: 2000 });
    setTimeout(() => setFieldCopied(false), 2000);
  };
  return (
    <Box
      bg="gray.900" borderRadius="lg" px={4} py={3.5} position="relative"
      border="1px solid" borderColor="gray.800"
    >
      <Text
        fontSize="xs" fontFamily="mono" color="gray.100"
        whiteSpace={multiline ? "pre-wrap" : "normal"}
        wordBreak="break-all" lineHeight="1.8" pr={8}
      >
        {value}
      </Text>
      <IconButton
        icon={fieldCopied ? <CheckCircleIcon color="green.300" /> : <CopyIcon color="gray.400" />}
        size="xs" variant="ghost" onClick={copyValue} aria-label="Copy"
        position="absolute" top={2.5} right={2.5}
        _hover={{ bg: "whiteAlpha.200" }}
      />
    </Box>
  );
};

const DocsLink = ({ slug, label }) => (
  <Link
    href={`/docs/${slug}`} target="_blank" rel="noopener noreferrer"
    fontSize="xs" fontWeight="600" color="brand.600"
    display="inline-flex" alignItems="center" gap={1}
    _hover={{ color: "brand.700", textDecoration: "underline" }}
  >
    {label}
    <ExternalLinkIcon boxSize={3} />
  </Link>
);

// Shown instead of a URL when no plan is selected (overview mode)
const NoPlanNotice = () => (
  <Box px={4} py={3} bg="brand.50" border="1px solid" borderColor="brand.100" borderRadius="lg">
    <Text fontSize="xs" color="brand.700" lineHeight="1.6">
      Open a plan, then <strong>Share &amp; Connect</strong> → <strong>Connect an AI tool</strong> to get the link for that plan.
    </Text>
  </Box>
);


const McpModal = ({ isOpen, onClose, currentPlanId, planName, initialTool }) => {
  const [mcpConnectionData, setMcpConnectionData] = useState(null);
  const [isMcpLoading, setIsMcpLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [designIndex, setDesignIndex] = useState(0);
  const [promptCopied, setPromptCopied] = useState(false);
  const [connections, setConnections] = useState(null);
  const installConfig = useClientInstallConfig(isOpen);
  const toast = useToast();

  // The plan's own link, used only by Figma Make.
  const fetchMcpToken = async () => {
    setIsMcpLoading(true);
    try {
      const response = await fetchWithAuth("/api/mcp/access-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: currentPlanId }),
      });
      if (!response.ok) throw new Error("Failed to generate access token");
      setMcpConnectionData(await response.json());
    } catch (error) {
      console.error("Error generating MCP token:", error);
      toast({ title: "Couldn’t prepare the Figma Make link", description: "Try again in a moment.", status: "error", duration: 3000 });
    } finally {
      setIsMcpLoading(false);
    }
  };

  // Overview mode: no plan, so no plan link and nothing connected to show.
  const isOverviewMode = !currentPlanId;

  useEffect(() => {
    let cancelled = false;
    if (isOpen && currentPlanId) {
      fetchMcpToken();
      // Tools already connected to this plan show their status, not install steps.
      fetchWithAuth(`/api/client-setup/plans/${currentPlanId}/connections`)
        .then((response) => (response.ok ? response.json() : { connections: [] }))
        .then((value) => { if (!cancelled) setConnections(value.connections || []); })
        .catch(() => { if (!cancelled) setConnections([]); });
    }
    if (isOpen) {
      const index = DESIGN_TOOLS.findIndex((tool) => tool.id === initialTool);
      setTabIndex(index >= 0 ? 1 : 0);
      if (index >= 0) setDesignIndex(index);
    } else {
      setMcpConnectionData(null);
      setConnections(null);
      setDesignIndex(0);
      setTabIndex(0);
      setPromptCopied(false);
    }
    return () => { cancelled = true; };
  }, [isOpen, initialTool]);

  const mcpUrl = mcpConnectionData?.mcp_url || "";
  const design = DESIGN_TOOLS[designIndex];
  const promptLines = {
    load: "Use get_full_context to load the complete strategy: goals, audience, constraints, and decisions. Then call get_pulse for the current strategic health.",
    rule: "Design components, flows, and screens that serve the strategic direction. No UI decisions that contradict the goals, audience, or constraints.",
    loop: "Before committing to a meaningful design direction, call ask_pabs_agent with the intent. If it returns decision_candidate, use decide_build_decision to commit, revise, or abandon it before continuing. After each session, use report_learning to capture what you observed.",
  };
  const fullPrompt = `Before designing, use your ${planName || "Pabs"} MCP connector tools to load my product strategy.\n\nLoad: ${promptLines.load}\n\nRule: ${promptLines.rule}\n\nLoop: ${promptLines.loop}`;

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(fullPrompt);
    setPromptCopied(true);
    toast({ title: "Prompt copied", description: "Paste this as your first message in the design tool", status: "success", duration: 2000 });
    setTimeout(() => setPromptCopied(false), 3000);
  };

  const syncedItems = [
    "Your Brief, strategy and decisions before it builds",
    "The next steps and blockers to work on",
    "Design Authority guidance for screens and flows",
    "Learnings sent back to Pabs and evaluated",
    "Build decisions captured for your approval",
    "Pulse. Strategic health at a glance",
  ];
  const riskItems = [
    "Agent contradicts earlier decisions",
    "Scope creep goes unchecked",
    "Requirements get hallucinated",
    "Wasted tokens and money on rework",
  ];

  const renderDesignConnect = () => {
    if (design.promptOnly) {
      return (
        <VStack align="stretch" spacing={4}>
          <Box px={4} py={3} bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg">
            <HStack spacing={2} mb={1}>
              <WarningIcon boxSize={3} color="orange.400" />
              <Text fontSize="xs" fontWeight="600" color="gray.700">MCP not supported yet</Text>
            </HStack>
            <Text fontSize="xs" color="gray.600" lineHeight="1.6">
              Stitch has no connector setup. Copy the starter prompt and paste it as your opening message so Stitch designs with your goals, constraints and audience in mind.
            </Text>
          </Box>
          <DocsLink slug={design.docs} label={design.docsLabel} />
        </VStack>
      );
    }
    if (isOverviewMode) {
      return (
        <VStack align="stretch" spacing={4}>
          <NoPlanNotice />
          <DocsLink slug={design.docs} label={design.docsLabel} />
        </VStack>
      );
    }
    if (isMcpLoading) return <HStack spacing={2}><Spinner size="sm" color="brand.600" /><Text fontSize="xs" color="gray.500">Preparing this plan’s link…</Text></HStack>;
    if (!mcpUrl) return <Text fontSize="xs" color="gray.600">The link for this plan isn’t available right now. Close and reopen this window to try again.</Text>;
    return (
      <VStack align="stretch" spacing={4}>
        <Box>
          <FieldLabel>Server URL</FieldLabel>
          <CopyField value={mcpUrl} />
        </Box>
        <Text fontSize="xs" color="gray.500" lineHeight="1.7">
          {design.where} Name it <strong>{planName || "Pabs"}</strong>.
        </Text>
        <Text fontSize="xs" color="gray.400" lineHeight="1.6">
          The token is in the URL, so no API key is needed. One link per plan.
        </Text>
        <DocsLink slug={design.docs} label={design.docsLabel} />
      </VStack>
    );
  };

  const renderDesignPrompt = () => (
    <VStack align="stretch" spacing={3}>
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" bg="gray.50" px={5} py={5}>
        <VStack align="stretch" spacing={5}>
          <Text fontSize="xs" color="gray.700" lineHeight="1.8">
            Before designing, use your <strong>{planName || "Pabs"}</strong> MCP connector tools to load my product strategy.
          </Text>
          {[{ label: "Load", text: promptLines.load }, { label: "Rule", text: promptLines.rule }, { label: "Loop", text: promptLines.loop }].map(({ label, text }) => (
            <Box key={label}>
              <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="widest" color="gray.400" mb={1.5}>{label}</Text>
              <Text fontSize="xs" color="gray.700" lineHeight="1.8">{text}</Text>
            </Box>
          ))}
        </VStack>
      </Box>
      <Button
        leftIcon={promptCopied ? <CheckCircleIcon boxSize={3} /> : <CopyIcon boxSize={3} />}
        onClick={copyPrompt} size="sm" colorScheme="brand" borderRadius="md" fontWeight="500" fontSize="xs" alignSelf="flex-start"
      >
        {promptCopied ? "Copied" : "Copy starter prompt"}
      </Button>
    </VStack>
  );

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        maxW={{base:"calc(100vw - 32px)",md:"calc(100vw - 100px)"}}
        maxH={{base:"calc(100vh - 32px)",md:"calc(100vh - 100px)"}}
        w={{base:"calc(100vw - 32px)",md:"calc(100vw - 100px)"}}
        borderRadius="xl"
        overflow="hidden"
        my={{base:"16px",md:"50px"}}
      >
        <ModalCloseButton zIndex={2} color="gray.500" top={3} right={3} />
        <ModalBody p={0} overflow="hidden">
          <Flex direction={{ base: "column", md: "row" }} h={{base:"calc(100vh - 32px)",md:"calc(100vh - 100px)"}}>

            {/* ── Left panel ── */}
            <Box
              w={{ base: "100%", md: "28%" }}
              bg="brand.800"
              color="white"
              p={{ base: 6, md: 8 }}
              display={{base:"none",md:"flex"}}
              flexDirection="column"
              justifyContent="flex-start"
              position="relative"
              overflow="hidden"
              overflowY="auto"
              flexShrink={0}
            >
              <Box position="absolute" top="-60px" right="-60px" w="200px" h="200px" borderRadius="full" bg="whiteAlpha.50" />
              <Box position="absolute" bottom="-40px" left="-40px" w="150px" h="150px" borderRadius="full" bg="whiteAlpha.50" />

              <VStack align="start" spacing={5} position="relative" zIndex={1}>
                <Box>
                  <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="wider" color="whiteAlpha.700" mb={2}>
                    Your AI tools
                  </Text>
                  <Text fontSize="xl" fontWeight="700" lineHeight="1.2" mb={2}>
                    Let Pabs guide your build
                  </Text>
                  <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.6">
                    Connect the AI tools you build and design with, so strategic judgment stays active while your product is being designed and built.
                  </Text>
                </Box>

                <Box w="100%">
                  <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="wider" color="whiteAlpha.600" mb={3}>
                    What your AI tool works with
                  </Text>
                  <VStack align="start" spacing={2}>
                    {syncedItems.map((item, i) => (
                      <HStack key={i} spacing={2} align="start">
                        <CheckCircleIcon boxSize={3} color="green.300" mt={1} />
                        <Text fontSize="sm" color="whiteAlpha.900">{item}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>

                <Box w="100%">
                  <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="wider" color="red.300" mb={3}>
                    What goes wrong without it
                  </Text>
                  <VStack align="start" spacing={2}>
                    {riskItems.map((item, i) => (
                      <HStack key={i} spacing={2} align="start">
                        <WarningIcon boxSize={3} color="red.300" mt={1} />
                        <Text fontSize="sm" color="whiteAlpha.900">{item}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>

                <HStack spacing={1} pt={1}>
                  <Text fontSize="xs" color="whiteAlpha.600">Want to learn more?</Text>
                  <Button
                    as="a" href="/docs/install-pabs" target="_blank"
                    size="xs" variant="link" color="whiteAlpha.800" fontWeight="500"
                    rightIcon={<ExternalLinkIcon boxSize={3} />} _hover={{ color: "white" }}
                  >
                    Install and setup guide
                  </Button>
                </HStack>
              </VStack>
            </Box>

            {/* ── Right panel ── */}
            <Box flex="1" minH={0} display="flex" flexDirection="column" bg="white" overflow="hidden">
              <Box flex="1" overflowY="auto" px={{ base: 6, md: 8 }} py={{ base: 6, md: 8 }}>
                <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="brand" isLazy>
                  <TabList borderColor="gray.100" mb={6}>
                    <Tab fontSize="md" fontWeight="600" px={1} mr={8}>Build tools</Tab>
                    <Tab fontSize="md" fontWeight="600" px={1}>Design tools</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel p={0} maxW="760px">
                      <Text fontSize="sm" color="gray.600" lineHeight="1.6" mb={5}>
                        {isOverviewMode
                          ? "Install Pabs in your project, then choose the plan it works with when you connect."
                          : <>When you connect, <strong>{planName || "this plan"}</strong> is already selected.</>}
                      </Text>
                      {clientInstallAvailable(installConfig) ? (
                        <BriefClientSetup
                          config={installConfig}
                          planId={currentPlanId}
                          planName={planName}
                          connectedClients={isOverviewMode ? null : (connections || [])}
                          initialClient={INSTALL_CLIENT_FOR[initialTool] || null}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          Installation isn’t available on this server right now. See the <Link href="/docs/install-pabs" textDecoration="underline">install guide</Link>.
                        </Text>
                      )}
                    </TabPanel>
                    <TabPanel p={0}>
                      <Text fontSize="sm" color="gray.600" lineHeight="1.6" mb={5}>
                        Figma Make and Google Stitch connect to {isOverviewMode ? "a plan" : "this plan"} directly, without installing Pabs.
                      </Text>
                      <HStack spacing={2} mb={6} wrap="wrap">
                        {DESIGN_TOOLS.map((tool, index) => (
                          <Button
                            key={tool.id} size="sm" variant={index === designIndex ? "solid" : "outline"}
                            colorScheme={index === designIndex ? "brand" : "gray"}
                            leftIcon={<ToolTile meta={tool} size="20px" />}
                            onClick={() => setDesignIndex(index)}
                          >
                            {tool.label}
                          </Button>
                        ))}
                      </HStack>
                      <Text fontSize="xs" color="gray.600" lineHeight="1.7" mb={5}>{design.desc}</Text>
                      <Flex direction={{ base: "column", lg: "row" }} gap={8} align="start">
                        <Box flex="1" minW={0} w="100%">
                          <StepLabel num="01" label="Connect" />
                          {renderDesignConnect()}
                        </Box>
                        <Box display={{ base: "none", lg: "block" }} alignSelf="stretch" w="1px" bg="gray.100" flexShrink={0} />
                        <Box flex="1" minW={0} w="100%">
                          <StepLabel num="02" label="Start your design session" />
                          {renderDesignPrompt()}
                        </Box>
                      </Flex>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            </Box>

          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default McpModal;
